'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── ANC SETS ────────────────────────────────────────────────────────────────

export interface AncSet {
  id: string;
  setName: string;
  location?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
}

function mapAncSet(row: any): AncSet {
  return {
    id: row.id,
    setName: row.set_name,
    location: row.location ?? undefined,
    isActive: row.is_active,
    notes: row.notes ?? undefined,
    createdAt: row.created_at,
  };
}

export const ancSetService = {
  async getAll(): Promise<AncSet[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('anc_sets')
        .select('*')
        .eq('is_active', true)
        .order('set_name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapAncSet);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    setName: string;
    location?: string;
    notes?: string;
    createdBy: string;
  }): Promise<AncSet | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('anc_sets')
        .insert({
          set_name: payload.setName,
          location: payload.location || null,
          notes: payload.notes || null,
          created_by: payload.createdBy,
        })
        .select('*')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapAncSet(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },
};

// ─── ANC TIER LOGS ────────────────────────────────────────────────────────────

export type AncTier = 'bottom' | 'middle' | 'top';

export interface AncTierLog {
  id: string;
  userId: string;
  setId: string;
  setName?: string;
  tier: AncTier;
  logDate: string;
  // Bottom
  leachateVolumeL?: number;
  leachateColour?: string;
  // Middle
  castingWeightKg?: number;
  beddingCondition?: string;
  moistureLevel?: string;
  phReading?: number;
  wormMigrationObserved?: boolean;
  // Top
  feedType?: string;
  feedQuantityKg?: number;
  newBeddingMaterial?: string;
  newBeddingQuantityKg?: number;
  // Shared
  notes?: string;
  loggedBy?: string;
  createdAt: string;
}

function mapAncTierLog(row: any): AncTierLog {
  return {
    id: row.id,
    userId: row.user_id,
    setId: row.set_id,
    setName: row.anc_sets?.set_name ?? undefined,
    tier: row.tier,
    logDate: row.log_date,
    leachateVolumeL: row.leachate_volume_l != null ? Number(row.leachate_volume_l) : undefined,
    leachateColour: row.leachate_colour ?? undefined,
    castingWeightKg: row.casting_weight_kg != null ? Number(row.casting_weight_kg) : undefined,
    beddingCondition: row.bedding_condition ?? undefined,
    moistureLevel: row.moisture_level ?? undefined,
    phReading: row.ph_reading != null ? Number(row.ph_reading) : undefined,
    wormMigrationObserved: row.worm_migration_observed ?? false,
    feedType: row.feed_type ?? undefined,
    feedQuantityKg: row.feed_quantity_kg != null ? Number(row.feed_quantity_kg) : undefined,
    newBeddingMaterial: row.new_bedding_material ?? undefined,
    newBeddingQuantityKg: row.new_bedding_quantity_kg != null ? Number(row.new_bedding_quantity_kg) : undefined,
    notes: row.notes ?? undefined,
    loggedBy: row.user_profiles?.full_name ?? undefined,
    createdAt: row.created_at,
  };
}

export const ancTierLogService = {
  async getAll(setId?: string): Promise<AncTierLog[]> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('anc_tier_logs')
        .select('*, user_profiles(full_name), anc_sets(set_name)')
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(200);
      if (setId) {
        query = query.eq('set_id', setId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapAncTierLog);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    userId: string;
    setId: string;
    tier: AncTier;
    logDate: string;
    leachateVolumeL?: number;
    leachateColour?: string;
    castingWeightKg?: number;
    beddingCondition?: string;
    moistureLevel?: string;
    phReading?: number;
    wormMigrationObserved?: boolean;
    feedType?: string;
    feedQuantityKg?: number;
    newBeddingMaterial?: string;
    newBeddingQuantityKg?: number;
    notes?: string;
  }): Promise<AncTierLog | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('anc_tier_logs')
        .insert({
          user_id: payload.userId,
          set_id: payload.setId,
          tier: payload.tier,
          log_date: payload.logDate,
          leachate_volume_l: payload.leachateVolumeL ?? null,
          leachate_colour: payload.leachateColour || null,
          casting_weight_kg: payload.castingWeightKg ?? null,
          bedding_condition: payload.beddingCondition || null,
          moisture_level: payload.moistureLevel || null,
          ph_reading: payload.phReading ?? null,
          worm_migration_observed: payload.wormMigrationObserved ?? false,
          feed_type: payload.feedType || null,
          feed_quantity_kg: payload.feedQuantityKg ?? null,
          new_bedding_material: payload.newBeddingMaterial || null,
          new_bedding_quantity_kg: payload.newBeddingQuantityKg ?? null,
          notes: payload.notes || null,
        })
        .select('*, user_profiles(full_name), anc_sets(set_name)')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapAncTierLog(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('anc_tier_logs').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },

  subscribeToChanges(callback: () => void) {
    const supabase = createClient();
    return supabase
      .channel('anc_tier_logs_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'anc_tier_logs' }, callback)
      .subscribe();
  },
};
